// Scene builders for every area, driven by the shared AreaPlan so what you
// see is exactly what movement collides with. Clubs are intimate boxes with
// a glowing booth; the surface (render/surface.ts) is the planet with the
// settlement dressed at the origin.

import type { Aabb, AreaPlan } from "@bebop/shared";
import * as THREE from "three";
import type { LightPhase } from "./atmosphere";
import { contactPools } from "./attire";
import {
  type AuthoredPlacement,
  disposeAuthoredObject,
  instanceAuthoredAsset,
} from "./authored-assets";
import { createCityScene } from "./city";
import { type AreaScene, boxFromAabb, hexToCss, NIGHT, signTexture, Tracker } from "./common";
import { dress, paint } from "./forge";
import { createSpaceScene } from "./space";
import { createSurfaceScene } from "./surface";
import { createUnderstreetScene } from "./understreet";

export type { AreaScene } from "./common";

/** Repeated source meshes share geometry; only their instance buffers live here. */
function authored(
  t: Tracker,
  scene: THREE.Object3D,
  id: string,
  placements: readonly AuthoredPlacement[],
): THREE.Group {
  const group = instanceAuthoredAsset(id, placements);
  scene.add(group);
  t.cleanup(() => disposeAuthoredObject(group));
  return group;
}

function atBox(box: Aabb, height?: number): AuthoredPlacement {
  return {
    x: (box.minX + box.maxX) / 2,
    y: 0,
    z: (box.minZ + box.maxZ) / 2,
    width: box.maxX - box.minX,
    depth: box.maxZ - box.minZ,
    height,
  };
}

function createClubScene(plan: AreaPlan): AreaScene {
  const t = new Tracker();
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(NIGHT.background);
  scene.fog = new THREE.Fog(NIGHT.background, 20, 45);

  const width = plan.halfWidth * 2;
  const depth = plan.halfDepth * 2;
  const accent = new THREE.Color(plan.accent);

  const floor = new THREE.Mesh(
    t.geo(new THREE.PlaneGeometry(width, depth)),
    // Wave 12: a board floor, not a value. The club was measured as the one
    // set the asset programme had never touched at all — "byte-identical
    // between the last two waves" — and it is the MVP wedge's own room.
    t.mat(
      dress(
        // The sheet owns roughness now (TriplanarOptions.orm): the material
        // factor is 1 so the plank's own band reaches the screen unmultiplied,
        // and the stray 0.1 metalness on a piece of wood is gone.
        new THREE.MeshStandardMaterial({ roughness: 1 }),
        "wood",
        NIGHT.floor,
        { meters: 2 },
      ),
    ),
  );
  floor.rotation.x = -Math.PI / 2;
  scene.add(floor);

  // (The old dev grid on the floor is gone — the round-2 critic named it
  // the club's one fiction-breaker. The colored washes own the floor now.)

  const wallMaterial = t.mat(
    paint(new THREE.MeshStandardMaterial({ roughness: 0.95 }), "plate", NIGHT.wall, {
      orm: true,
      meters: 4,
      normalScale: 0.85,
    }),
  );
  const wt = 0.3;
  for (const wall of shellWalls(plan, wt)) {
    scene.add(boxFromAabb(t, wall, plan.wallHeight, wallMaterial));
  }

  // The shared AABBs still own collision; Blender owns cabinet and deck form.
  const booth = plan.booth;
  authored(
    t,
    scene,
    "speaker-stack",
    plan.obstacles.filter((o) => o !== booth).map((o) => atBox(o, 2.2)),
  );

  let strip: THREE.Mesh<THREE.BoxGeometry, THREE.MeshStandardMaterial> | undefined;
  let liveSign: THREE.Sprite | undefined;
  let boothLight: THREE.PointLight | undefined;
  if (booth) {
    authored(t, scene, "dj-booth", [atBox(booth)]);

    const stripMat = t.mat(
      new THREE.MeshStandardMaterial({
        color: plan.accent,
        emissive: plan.accent,
        emissiveIntensity: 1.4,
      }),
    );
    strip = new THREE.Mesh(
      t.geo(new THREE.BoxGeometry(booth.maxX - booth.minX, 0.08, 0.08)),
      stripMat,
    );
    strip.position.set((booth.minX + booth.maxX) / 2, 1.12, booth.maxZ + 0.02);
    scene.add(strip);

    boothLight = new THREE.PointLight(plan.accent, 22.5, 12, 1.6);
    boothLight.position.set((booth.minX + booth.maxX) / 2, 2.2, booth.maxZ + 1);
    scene.add(boothLight);

    // "· LIVE" billboard, shown while a DJ is on.
    const liveTex = signTexture(t, "· LIVE", hexToCss(NIGHT.liveGlow));
    liveSign = new THREE.Sprite(
      t.mat(new THREE.SpriteMaterial({ map: liveTex, transparent: true, depthWrite: false })),
    );
    liveSign.scale.set(2.6, 0.49, 1);
    liveSign.position.set((booth.minX + booth.maxX) / 2, 2.9, booth.maxZ - 0.8);
    liveSign.visible = false;
    scene.add(liveSign);
  }

  // The pile of flyers by the door: a low table with paper on it. Small,
  // deliberately not an obstacle — you walk up to it, not around it.
  const stack = plan.flyerStack;
  if (stack) {
    authored(t, scene, "flyer-table", [{ x: stack.x, y: 0, z: stack.z }]);
    const paper = new THREE.Mesh(
      t.geo(new THREE.BoxGeometry(0.6, 0.06, 0.42)),
      t.mat(
        new THREE.MeshStandardMaterial({
          color: 0xe8dcc0,
          roughness: 0.95,
          emissive: 0xe8dcc0,
          emissiveIntensity: 0.22,
        }),
      ),
    );
    paper.position.set(stack.x, 0.78, stack.z);
    paper.rotation.y = 0.24;
    scene.add(paper);
  }

  // Exit doorway: a lit frame around each portal on the south wall.
  for (const portal of plan.portals) scene.add(...doorFrame(t, plan, portal.zone));

  dressClubRoom(t, scene, plan);

  // THE INTERIOR REGIME, PUT BACK (wave 12).
  //
  // This vista is the project's own interior regression guard — "eternal
  // night, crushed-dark regime (YAVG ~17-21) must survive outdoor grading
  // changes" (plans/gauntlet/w9-vistas.json) — and the reference's ship
  // interior holds 19–25 with 63–73% of the plate under Y16. The riveted
  // plate work the last wave landed on the ceiling and walls came with a
  // flat-ambient raise that took the room to YAVG 30.2 and collapsed the
  // sub-16 mass to 37.0%: the plate was legible everywhere at once, which
  // is exactly what a crushed-dark interior is not.
  //
  // Only the two FLAT terms move. The three drifting washes, the booth
  // light and every emissive stay untouched, because they are what carves
  // the plate — a flat ambient does not model a rivet, it fills the crease
  // beside one. Cutting them to a third leaves the lit ceiling panels
  // exactly as bright and lets the far corners go where the law says.
  //
  // WAVE 15 — AND THEY MOVE AGAIN, FOR A REASON THAT IS NOT ABOUT THEM. The
  // response set (6191bd8/58b53e7) flipped `triplanar()`'s `orm` default from
  // false to true, so 29 of 62 materials began reading their own roughness
  // and metalness maps for the first time. In a room whose whole read is
  // SPECULAR — YAVG 17–21, three lamps 60 cm off the plate — that lands as a
  // brightening, and the two biggest terms are both in here: the wood floor's
  // `metalness: 0.1` now multiplies a timber ORM whose metal channel is 0, so
  // its diffuse gained a flat 1/(1−0.1) = +11% over the largest surface in
  // frame, and the steel sheet's roughness median fell 0.74 → 0.62 with a p2
  // of 0.29, which is a real specular lobe where there was a matte one. The
  // measured drift was 19.5 → 21.0 (Rec.709), i.e. out of the stated band by
  // 0.0–1.0 depending on where you round.
  //
  // The trim is measured, and the measurement said something useful: cutting
  // BOTH flat terms by a third moved the plate 0.1 luma. They are not where
  // the room's light is. The room is four lamps, most of the read is their
  // specular, and what the response set changed is how much of that specular
  // the surfaces return — so the surfaces are now over-lit for the rig, and
  // the honest correction is the rig. The lamps come down ~10% (below), the
  // flat terms take a smaller cut than the first cut of this fix tried, and
  // the emissives — the strip, the bottles, the par-can lenses — are left
  // exactly alone, because they are the only things in here that are
  // supposed to be sources.
  scene.add(new THREE.AmbientLight(0x8888aa, 0.115));
  scene.add(new THREE.HemisphereLight(0x3a3a55, 0x101018, 0.17));

  // Club lights washing the floor; hues drift slowly around the accent.
  //
  // The REACH is what came in for wave 12, not the brightness. Each of these
  // hangs 60 cm under the ceiling, so a plate directly above a lamp is a few
  // tens of centimetres away and its inverse-square term is enormous — the
  // riveted panels the judges praised do not care what the cutoff radius is.
  // The far floor, the back wall and the corners are 8–14 m out, where the
  // cutoff is the ONLY thing deciding whether they are lit or dark, and at
  // r=22/decay=1.8 they were all comfortably lit. That is a room with even
  // illumination, which is the opposite of the reference's ship interior
  // (63–73% of the plate under Y16) and of this vista's own written law.
  // Pulling the radius to 16 and steepening the falloff keeps every pool
  // exactly as hot at its centre and lets the room go dark between them.
  const clubLights = [
    // WAVE 15 — 60/60/40 → 54/54/36. See the ambient note above: the room
    // drifted to Rec.709 21.8 against a stated 17–21 when 29 materials began
    // reading their own roughness maps, and a surface that returns more light
    // under the same lamp is a lamp that is now too bright.
    { light: new THREE.PointLight(0xff2d95, 54, 16, 2.15), hue: 0.92 },
    { light: new THREE.PointLight(0x24d1e8, 54, 16, 2.15), hue: 0.52 },
    {
      light: new THREE.PointLight(plan.accent, 36, 15, 2.15),
      hue: accent.getHSL({ h: 0, s: 0, l: 0 }).h,
    },
  ];
  const lightSpread = plan.halfWidth * 0.5;
  clubLights[0]?.light.position.set(-lightSpread, plan.wallHeight - 0.6, -2);
  clubLights[1]?.light.position.set(lightSpread, plan.wallHeight - 0.6, -2);
  clubLights[2]?.light.position.set(0, plan.wallHeight - 0.6, plan.halfDepth * 0.45);
  for (const { light } of clubLights) scene.add(light);

  // Indoors, world coordinates ARE render coordinates.
  const worldAnchor = new THREE.Group();
  scene.add(worldAnchor);

  let elapsed = 0;
  let live = false;
  const color = new THREE.Color();
  const standbyGlow = new THREE.Color(plan.accent);
  const liveGlow = new THREE.Color(NIGHT.liveGlow);

  return {
    scene,
    worldAnchor,
    cameraFar: 100,
    origin: () => ({ x: 0, z: 0 }),
    groundY: () => 0,
    tick(dt: number, level = 0) {
      elapsed += dt;
      for (const entry of clubLights) {
        const hue = (entry.hue + elapsed * 0.02) % 1;
        entry.light.color.copy(color.setHSL(hue, 0.85, 0.55));
      }
      if (!strip || !boothLight || !liveSign) return;
      if (live) {
        strip.material.emissiveIntensity = 1.6 + level * 4 + Math.sin(elapsed * 6) * 0.2;
        liveSign.material.opacity = 0.8 + Math.sin(elapsed * 3.2) * 0.2;
        boothLight.intensity = 29 + level * 27;
      } else {
        strip.material.emissiveIntensity = 1.2 + Math.sin(elapsed * 2.1) * 0.35;
        boothLight.intensity = 22.5;
      }
    },
    setNights() {
      // The wall is out on the street.
    },
    setLive(next: boolean) {
      if (next === live) return;
      live = next;
      if (!strip || !boothLight || !liveSign) return;
      liveSign.visible = next;
      strip.material.color.copy(next ? liveGlow : standbyGlow);
      strip.material.emissive.copy(next ? liveGlow : standbyGlow);
      boothLight.color.copy(next ? liveGlow : standbyGlow);
    },
    dispose() {
      t.dispose();
    },
  };
}

/**
 * The workshop: the same interior shell as a club, lit like a place where
 * somebody works. No colour-cycling lights, no live sign — a long bench
 * under a warm lamp, and a brass strip along its front edge so the room
 * reads as "come and stand here" the moment you walk in.
 */
function createWorkshopScene(plan: AreaPlan): AreaScene {
  const t = new Tracker();
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(NIGHT.background);
  scene.fog = new THREE.Fog(NIGHT.background, 18, 40);

  const width = plan.halfWidth * 2;
  const depth = plan.halfDepth * 2;

  const floor = new THREE.Mesh(
    t.geo(new THREE.PlaneGeometry(width, depth)),
    t.mat(dress(new THREE.MeshStandardMaterial({ roughness: 1 }), "wood", 0x30281f, { meters: 2 })),
  );
  floor.rotation.x = -Math.PI / 2;
  scene.add(floor);

  const wallMaterial = t.mat(
    paint(new THREE.MeshStandardMaterial({ roughness: 1 }), "plate", 0x292b2b, {
      orm: true,
      meters: 3,
    }),
  );
  const wt = 0.3;
  for (const wall of shellWalls(plan, wt)) {
    scene.add(boxFromAabb(t, wall, plan.wallHeight, wallMaterial));
  }

  // Each trade has an authored tool silhouette over its existing solid box.
  const bench = plan.bench;

  let lampGlow: THREE.PointLight | undefined;
  if (bench) {
    const count = Math.max(1, Math.round((bench.maxX - bench.minX) / 2.88));
    const span = (bench.maxX - bench.minX) / count;
    authored(
      t,
      scene,
      "workbench",
      Array.from({ length: count }, (_, i) => ({
        x: bench.minX + span * (i + 0.5),
        y: 0,
        z: (bench.minZ + bench.maxZ) / 2,
        width: span - 0.025,
        depth: bench.maxZ - bench.minZ,
      })),
    );
    const stripMat = t.mat(
      new THREE.MeshStandardMaterial({
        color: plan.accent,
        emissive: plan.accent,
        emissiveIntensity: 1.1,
      }),
    );
    const strip = new THREE.Mesh(
      t.geo(new THREE.BoxGeometry(bench.maxX - bench.minX, 0.07, 0.07)),
      stripMat,
    );
    strip.position.set((bench.minX + bench.maxX) / 2, 1.07, bench.maxZ + 0.02);
    scene.add(strip);

    lampGlow = new THREE.PointLight(0xffc46b, 34, 16, 1.5);
    lampGlow.position.set((bench.minX + bench.maxX) / 2, 2.4, bench.maxZ + 0.9);
    scene.add(lampGlow);

    const sign = signTexture(t, "WORKSHOP", hexToCss(plan.accent));
    const board = new THREE.Sprite(
      t.mat(new THREE.SpriteMaterial({ map: sign, transparent: true, depthWrite: false })),
    );
    board.scale.set(3.2, 0.6, 1);
    board.position.set((bench.minX + bench.maxX) / 2, 2.85, bench.maxZ + 0.12);
    scene.add(board);
  }

  // The other stations (crafting v2): the luthier's rack, the tailor's
  // table, the joiner's bench, the copper still — solid slabs against the
  // side walls with a small sign each, so the room reads as four trades
  // the moment you walk in. Geometry only; the panel is the interface.
  for (const station of plan.stations ?? []) {
    const id =
      {
        luthier: "luthier-station",
        tailor: "tailor-station",
        joinery: "joinery-station",
        still: "copper-still",
      }[station.id] ?? "workbench";
    const west = station.box.minX < 0;
    // Stations are authored along X; quarter-turn the whole source so tools
    // face the aisle, and fit local width/depth to the same collision box.
    authored(t, scene, id, [
      {
        ...atBox(station.box),
        width: station.box.maxZ - station.box.minZ,
        depth: station.box.maxX - station.box.minX,
        yaw: west ? Math.PI / 2 : -Math.PI / 2,
      },
    ]);
    const stationSign = signTexture(t, station.label.toUpperCase(), hexToCss(plan.accent));
    const stationBoard = new THREE.Sprite(
      t.mat(new THREE.SpriteMaterial({ map: stationSign, transparent: true, depthWrite: false })),
    );
    stationBoard.scale.set(2.6, 0.5, 1);
    stationBoard.position.set(
      (station.box.minX + station.box.maxX) / 2,
      2.6,
      (station.box.minZ + station.box.maxZ) / 2,
    );
    stationBoard.position.x += west ? 0.6 : -0.6;
    scene.add(stationBoard);
  }

  for (const portal of plan.portals) scene.add(...doorFrame(t, plan, portal.zone));
  dressInteriorWalls(t, scene, plan);
  interiorCeiling(t, scene, plan, wallMaterial);

  // A club is lit by three moving lamps and reads fine on ambient alone;
  // a workshop lit only at the bench renders as a black box from the
  // doorway, which is exactly what it did on the first run. So: a room
  // light over the floor, and enough ambient to see the walls you walked
  // in through.
  const roomLight = new THREE.PointLight(0xffe0b0, 26, 26, 1.4);
  roomLight.position.set(0, plan.wallHeight - 0.5, plan.halfDepth * 0.2);
  scene.add(roomLight);
  scene.add(new THREE.AmbientLight(0xa89a80, 0.85));
  scene.add(new THREE.HemisphereLight(0x6a5c48, 0x1a1712, 0.9));

  const worldAnchor = new THREE.Group();
  scene.add(worldAnchor);

  let elapsed = 0;
  return {
    scene,
    worldAnchor,
    cameraFar: 90,
    origin: () => ({ x: 0, z: 0 }),
    groundY: () => 0,
    tick(dt: number) {
      // A working lamp, not a light show: a slow flicker and nothing else.
      elapsed += dt;
      if (lampGlow) lampGlow.intensity = 22 + Math.sin(elapsed * 1.7) * 1.6;
    },
    setLive() {
      // Nothing goes live in here.
    },
    setNights() {
      // The wall is out on the street.
    },
    dispose() {
      t.dispose();
    },
  };
}

/**
 * A home interior: the mood law applied to a room somebody owns — the
 * crushed-dark regime of every Bebop interior, lifted by warm emissives
 * only: a hearth glow on the north wall, a lintel lamp at the door, and
 * whatever lamps the owner placed (those ride in with the furniture
 * layer). Deliberately dimmer than the workshop: a home is lit by its own
 * things, and an empty cabin SHOULD read bare until it's furnished.
 */
function createHomeScene(plan: AreaPlan): AreaScene {
  const t = new Tracker();
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(NIGHT.background);
  scene.fog = new THREE.Fog(NIGHT.background, 14, 34);

  const width = plan.halfWidth * 2;
  const depth = plan.halfDepth * 2;

  // Plank floor: warmer than the club's concrete.
  const floor = new THREE.Mesh(
    t.geo(new THREE.PlaneGeometry(width, depth)),
    t.mat(dress(new THREE.MeshStandardMaterial({ roughness: 1 }), "wood", 0x3b2d20, { meters: 2 })),
  );
  floor.rotation.x = -Math.PI / 2;
  scene.add(floor);

  const wallMaterial = t.mat(
    dress(new THREE.MeshStandardMaterial({ roughness: 1 }), "wood", 0x382b21, { meters: 2.5 }),
  );
  const wt = 0.3;
  for (const wall of shellWalls(plan, wt)) {
    scene.add(boxFromAabb(t, wall, plan.wallHeight, wallMaterial));
  }

  if (plan.kind !== "clinic")
    authored(t, scene, "hearth", [{ x: 0, y: 0, z: -plan.halfDepth + 0.55 }]);
  const hearthLight = new THREE.PointLight(0xff9b50, 18, 14, 1.6);
  hearthLight.position.set(0, 1.3, -plan.halfDepth + 1.0);
  scene.add(hearthLight);

  for (const portal of plan.portals) scene.add(...doorFrame(t, plan, portal.zone));
  dressInteriorWalls(t, scene, plan);
  interiorCeiling(t, scene, plan, wallMaterial);

  // Enough ambient to see the walls; the warmth comes from the fire, the
  // lintel, and the owner's lamps.
  scene.add(new THREE.AmbientLight(0x9a8a70, 0.55));
  scene.add(new THREE.HemisphereLight(0x584c3a, 0x14100c, 0.7));
  const doorGlow = new THREE.PointLight(0xffc46b, 10, 8, 1.6);
  doorGlow.position.set(0, 2.3, plan.halfDepth - 0.6);
  scene.add(doorGlow);

  const worldAnchor = new THREE.Group();
  scene.add(worldAnchor);

  let elapsed = 0;
  return {
    scene,
    worldAnchor,
    cameraFar: 80,
    origin: () => ({ x: 0, z: 0 }),
    groundY: () => 0,
    tick(dt: number) {
      // The fire breathes; nothing else moves unless the owner moves it.
      elapsed += dt;
      hearthLight.intensity = 16 + Math.sin(elapsed * 2.3) * 2.2 + Math.sin(elapsed * 5.1) * 0.9;
    },
    setLive() {
      // Nothing goes live at home.
    },
    setNights() {
      // The wall is out on the street.
    },
    dispose() {
      t.dispose();
    },
  };
}

/** Club architecture and furnishings come from the editable interior source.
 * Room spans, portal positions and the existing contact treatment stay local.
 */
function dressClubRoom(t: Tracker, scene: THREE.Scene, plan: AreaPlan): void {
  const hw = plan.halfWidth;
  const hd = plan.halfDepth;
  const h = plan.wallHeight;
  const panel = t.mat(
    paint(new THREE.MeshStandardMaterial({ roughness: 0.9 }), "plate", 0x1e1c26, {
      orm: true,
      meters: 4,
    }),
  );
  const ceiling = new THREE.Mesh(t.geo(new THREE.PlaneGeometry(hw * 2, hd * 2)), panel);
  ceiling.rotation.x = Math.PI / 2;
  ceiling.position.y = h;
  scene.add(ceiling);

  const trusses: AuthoredPlacement[] = [];
  const cans: AuthoredPlacement[] = [];
  const trussY = h - 0.81;
  for (const z of [-hd * 0.42, hd * 0.42]) {
    const count = Math.ceil((hw * 2 - 0.4) / 2);
    const span = (hw * 2 - 0.4) / count;
    for (let i = 0; i < count; i++)
      trusses.push({ x: -hw + 0.2 + span * (i + 0.5), y: trussY, z, width: span });
    for (let i = 0; i < 6; i++)
      cans.push({ x: -hw * 0.8 + (i / 5) * hw * 1.6, y: trussY - 0.44, z });
  }
  authored(t, scene, "truss", trusses);
  authored(t, scene, "par-can", cans);
  dressInteriorWalls(t, scene, plan);

  const barZ0 = -hd * 0.45;
  const barZ1 = hd * 0.55;
  const barLen = barZ1 - barZ0;
  const barX = hw - 0.62;
  const count = Math.max(2, Math.round(barLen / 2));
  const span = barLen / count;
  const counters: AuthoredPlacement[] = [];
  const shelves: AuthoredPlacement[] = [];
  for (let i = 0; i < count; i++) {
    const z = barZ0 + span * (i + 0.5);
    counters.push({ x: barX, y: 0, z, depth: span });
    shelves.push({ x: hw - 0.2, y: 0, z, depth: span - 0.025 });
  }
  authored(t, scene, "bar-counter", counters);
  authored(t, scene, "back-bar", shelves);
  authored(
    t,
    scene,
    "bar-stool",
    Array.from({ length: 5 }, (_, i) => ({
      x: barX - 0.95,
      y: 0,
      z: barZ0 + 0.9 + (i / 4) * (barLen - 1.8),
    })),
  );

  const contacts: Array<{ x: number; z: number; r: number }> = [];
  for (const o of plan.obstacles)
    contacts.push({
      x: (o.minX + o.maxX) / 2,
      z: (o.minZ + o.maxZ) / 2,
      r: Math.max(o.maxX - o.minX, o.maxZ - o.minZ) * 2.3,
    });
  if (plan.booth) {
    const booth = plan.booth;
    authored(t, scene, "equipment-rack", [
      {
        x: (booth.minX + booth.maxX) / 2,
        y: 1.15,
        z: booth.minZ + 0.3,
        width: booth.maxX - booth.minX + 0.6,
      },
    ]);
  }
  contacts.push({ x: barX, z: (barZ0 + barZ1) / 2, r: barLen * 1.15 });
  if (plan.flyerStack) contacts.push({ x: plan.flyerStack.x, z: plan.flyerStack.z, r: 2 });
  const pools = contactPools(contacts.length, 0.6, 0.42);
  t.mat(pools.material as THREE.Material);
  const dummy = new THREE.Object3D();
  contacts.forEach((c, i) => {
    dummy.position.set(c.x, 0.02, c.z);
    dummy.rotation.set(-Math.PI / 2, 0, 0);
    dummy.scale.set(c.r, c.r, 1);
    dummy.updateMatrix();
    pools.setMatrixAt(i, dummy.matrix);
  });
  t.cleanup(() => pools.dispose());
  scene.add(pools);
}

/** Shallow authored pilasters keep every portal and walkable span unchanged. */
function dressInteriorWalls(t: Tracker, scene: THREE.Scene, plan: AreaPlan): void {
  const placements: AuthoredPlacement[] = [];
  const { halfWidth: hw, halfDepth: hd, wallHeight: h } = plan;
  const xbays = Math.max(3, Math.round(hw / 1.75));
  for (let i = 0; i <= xbays; i++) {
    const x = -hw + 0.25 + (i / xbays) * (hw * 2 - 0.5);
    for (const z of [-hd + 0.035, hd - 0.035]) {
      if (z > 0 && plan.portals.some((p) => x > p.zone.minX - 0.4 && x < p.zone.maxX + 0.4))
        continue;
      placements.push({ x, y: 0, z, height: h - 0.08, yaw: z > 0 ? Math.PI : 0 });
    }
  }
  const zbays = Math.max(3, Math.round(hd / 1.75));
  for (let i = 1; i < zbays; i++) {
    const z = -hd + (i / zbays) * hd * 2;
    for (const x of [-hw + 0.035, hw - 0.035])
      placements.push({ x, y: 0, z, height: h - 0.08, yaw: x > 0 ? -Math.PI / 2 : Math.PI / 2 });
  }
  authored(t, scene, "wall-pilaster", placements);
}

function interiorCeiling(
  t: Tracker,
  scene: THREE.Scene,
  plan: AreaPlan,
  material: THREE.Material,
): void {
  const ceiling = new THREE.Mesh(
    t.geo(new THREE.PlaneGeometry(plan.halfWidth * 2, plan.halfDepth * 2)),
    material,
  );
  ceiling.rotation.x = Math.PI / 2;
  ceiling.position.y = plan.wallHeight;
  scene.add(ceiling);
}

/** The infirmary keeps its recovery zone, now visibly furnished with cots. */
function createClinicScene(plan: AreaPlan): AreaScene {
  const room = createHomeScene(plan);
  const t = new Tracker();
  const cots: AuthoredPlacement[] = [];
  for (const box of plan.obstacles) {
    const count = Math.max(1, Math.floor((box.maxX - box.minX) / 1.35));
    const span = (box.maxX - box.minX) / count;
    for (let i = 0; i < count; i++)
      cots.push({
        x: box.minX + span * (i + 0.5),
        y: 0,
        z: (box.minZ + box.maxZ) / 2,
        width: Math.min(0.88, span - 0.12),
        depth: box.maxZ - box.minZ,
      });
  }
  authored(t, room.scene, "cot", cots);
  const cabinetZ = plan.obstacles[0]?.maxZ ?? -plan.halfDepth + 1.8;
  authored(t, room.scene, "medical-cabinet", [
    { x: plan.halfWidth - 0.55, y: 0, z: cabinetZ + 0.7, yaw: -Math.PI / 2 },
  ]);
  const dispose = room.dispose;
  return {
    ...room,
    dispose() {
      t.dispose();
      dispose();
    },
  };
}

/** The four walls of an interior box, as boxes. */
function shellWalls(plan: AreaPlan, wt: number): Aabb[] {
  return [
    {
      minX: -plan.halfWidth - wt,
      maxX: plan.halfWidth + wt,
      minZ: -plan.halfDepth - wt,
      maxZ: -plan.halfDepth,
    },
    {
      minX: -plan.halfWidth - wt,
      maxX: plan.halfWidth + wt,
      minZ: plan.halfDepth,
      maxZ: plan.halfDepth + wt,
    },
    {
      minX: -plan.halfWidth - wt,
      maxX: -plan.halfWidth,
      minZ: -plan.halfDepth,
      maxZ: plan.halfDepth,
    },
    {
      minX: plan.halfWidth,
      maxX: plan.halfWidth + wt,
      minZ: -plan.halfDepth,
      maxZ: plan.halfDepth,
    },
  ];
}

/** A lit frame around a portal on the south wall. */
function doorFrame(t: Tracker, plan: AreaPlan, zone: Aabb): THREE.Object3D[] {
  const frameMat = t.mat(
    new THREE.MeshStandardMaterial({
      color: plan.accent,
      emissive: plan.accent,
      emissiveIntensity: 0.9,
    }),
  );
  const doorW = zone.maxX - zone.minX;
  const parts: THREE.Object3D[] = [];
  const top = new THREE.Mesh(t.geo(new THREE.BoxGeometry(doorW + 0.3, 0.14, 0.14)), frameMat);
  top.position.set((zone.minX + zone.maxX) / 2, 2.4, plan.halfDepth + 0.05);
  parts.push(top);
  for (const side of [zone.minX - 0.08, zone.maxX + 0.08]) {
    const jamb = new THREE.Mesh(t.geo(new THREE.BoxGeometry(0.14, 2.4, 0.14)), frameMat);
    jamb.position.set(side, 1.2, plan.halfDepth + 0.05);
    parts.push(jamb);
  }
  return parts;
}

export function createAreaScene(plan: AreaPlan, phase: LightPhase = "day"): AreaScene {
  // Surface FIRST, then the interiors that have their own dressing, and
  // the club shell as the fallback. Written this way round deliberately:
  // dispatching on the interiors and falling through to the surface means
  // a newly added room kind renders as a chunk of open planet inside a
  // box, which is silent and very easy to ship. Falling through to the
  // shell instead just gives the new room plain walls. Only the frontier
  // surface consults the lighting phase — clubs, the Black, and Meridian's
  // fixed noon are their own regimes by design.
  if (plan.kind === "surface") {
    // Which planet's daylight this is: the frontier's wilds, or
    // Meridian's bone-white noon. Same kind, different world, different
    // ground and sky — the dispatch the worlds module exists for.
    return plan.world === "meridian" ? createCityScene(plan) : createSurfaceScene(plan, phase);
  }
  if (plan.kind === "space") return createSpaceScene(plan);
  if (plan.kind === "district") return createUnderstreetScene(plan);
  if (plan.kind === "workshop") return createWorkshopScene(plan);
  if (plan.kind === "home") return createHomeScene(plan);
  if (plan.kind === "clinic") return createClinicScene(plan);
  return createClubScene(plan);
}
